import { gray, white } from '@synoptic/ui-kit/palette.ts';
import { smTokens } from '@synoptic/ui-kit/theme/semantic-vars.css.js';
import {
  useThemeColors,
  useThemeMode,
} from '@synoptic/ui-kit/theme/theme-context.js';
import { fonts } from '@synoptic/ui-kit/tokens.js';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import Sigma from 'sigma';
import { EdgeLineProgram, NodeCircleProgram } from 'sigma/rendering';
import { CameraState } from 'sigma/types';
import { getCommunityColor } from './get-commnity-color';
import { useGraph } from './graph-provider';
import { NoopNodeProgram } from './nodes/node-noop';
import { makeDrawNodeHover, makeDrawNodeLabel } from './sigma-drawing-fns';
import { sigmaInstanceVar } from './sigma-options-var';
import { useSigmaHighlightHovered } from './use-sigma-highlight-hovered';
import { graphContainer } from '../route.css';

export const SigmaGraph = ({
  preview,
  initialCameraState,
}: {
  preview?: boolean;
  initialCameraState?: CameraState;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sigma, setSigma] = useState<Sigma | null>(null);

  const themeColors = useThemeColors();
  const themeMode = useThemeMode();

  const labelOutlineColor = themeMode === 'light' ? white[900] : gray[900];

  const graph = useGraph();

  useEffect(() => {
    if (!containerRef.current) return;
    let sigma: Sigma;

    try {
      sigma = new Sigma(graph, containerRef.current, {
        labelSize: preview ? 7 : 10,
        labelFont: fonts.sans,
        labelDensity: preview ? 2 : 2,
        labelGridCellSize: preview ? 60 : 100,
        labelRenderedSizeThreshold: preview ? 0 : 2,
        defaultEdgeType: 'edges-fast',
        nodeProgramClasses: {
          circle: NodeCircleProgram,
        },
        nodeHoverProgramClasses: {
          circle: NoopNodeProgram,
        },
        edgeProgramClasses: {
          'edges-fast': EdgeLineProgram,
        },
        itemSizesReference: 'positions',
        zoomToSizeRatioFunction: (x) => x,
      });

      const afterRender = () => {
        // @ts-expect-error PAGE_READY is not defined
        window.PAGE_READY = true;
      };
      sigma.once('afterRender', afterRender);

      setSigma(sigma);
      sigmaInstanceVar(sigma);
    } catch (error) {
      console.error('Error initializing Sigma.js:', error);
    }

    return () => {
      if (sigma) {
        sigma.kill();
        sigmaInstanceVar(null);
      }
    };
  }, [graph, preview]);

  const disabledColor =
    themeMode === 'light' ? smTokens.surface.disabled : gray[700];

  const [searchParams] = useSearchParams();
  const selectedNode = searchParams.get('node');
  const edgeDirection = searchParams.get('dir') === 'out' ? 'out' : 'in';

  useEffect(() => {
    if (!preview || !sigma) return;

    const camera = sigma.getCamera();

    if (initialCameraState) {
      camera.setState(initialCameraState);
    } else if (selectedNode) {
      const displayData = sigma.getNodeDisplayData(selectedNode);

      camera.updateState(() => ({
        ...displayData,
        ratio: 0.4,
      }));
    } else {
      camera.updateState((state) => ({
        ...state,
        ratio: 0.5,
      }));
    }
  }, [initialCameraState, preview, selectedNode, sigma]);

  useEffect(() => {
    if (!sigma) {
      return;
    }
    const start = performance.now();

    const graph = sigma.getGraph();

    sigma.setSettings({
      labelColor: {
        color: themeColors.container.foreground.primary,
      },
      defaultEdgeColor: themeMode === 'light' ? gray[400] : gray[500],
      defaultDrawNodeLabel: makeDrawNodeLabel(labelOutlineColor, preview),
      defaultDrawNodeHover: makeDrawNodeHover(labelOutlineColor),
      nodeReducer: (node, data) => {
        if (selectedNode) {
          if (
            node !== selectedNode &&
            !(edgeDirection === 'out'
              ? graph.areOutNeighbors(selectedNode, node)
              : graph.areInNeighbors(selectedNode, node))
          ) {
            return {
              ...data,
              color: disabledColor,
              label: null,
            };
          }
        }

        const color = getCommunityColor(themeMode, data.community);

        if (node === selectedNode) {
          return {
            ...data,
            color,
            highlighted: true,
          };
        }

        return {
          ...data,
          color,
        };
      },
      edgeReducer: (_edge, data) => {
        if (
          selectedNode &&
          (edgeDirection === 'out'
            ? data.id.startsWith(selectedNode)
            : data.id.endsWith(selectedNode))
        ) {
          return {
            ...data,
            hidden: false,
          };
        }

        return data;
      },
    });
    const end = performance.now();
    console.log('set settings', end - start, 'ms');
  }, [
    disabledColor,
    edgeDirection,
    labelOutlineColor,
    preview,
    selectedNode,
    sigma,
    themeColors.badge.disabled.background,
    themeColors.container.foreground.primary,
    themeMode,
  ]);

  useSigmaHighlightHovered();

  return <div className={graphContainer} ref={containerRef} />;
};
